import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, buttonClass, cx, Icon, Panel } from '../../ui/index.ts';

/**
 * Presentational pieces shared by the served-document renderer. They
 * follow the handwritten page's Callout / SnippetCard / DocsSection
 * idioms (Field Notes tokens only) so the two views sit in the same
 * visual family until #152 deletes the handwritten copy.
 */

export function DocsSection({
  id,
  title,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div id={id} className="scroll-mt-4" data-testid={`docs-section-${id}`}>
      <Panel caption={title} padded>
        <div className="flex flex-col gap-4">{children}</div>
      </Panel>
    </div>
  );
}

export function DocsCallout({
  children,
  tone,
  title,
}: {
  readonly children: ReactNode;
  readonly tone: 'note' | 'warning' | 'fact';
  readonly title?: string | null;
}): JSX.Element {
  if (tone === 'fact') {
    return (
      <article className="rounded-lg border border-line-soft bg-well p-4">
        {title !== undefined && title !== null && title.length > 0 ? (
          <h3 className="m-0 font-mono text-body font-medium text-forest">{title}</h3>
        ) : null}
        <div
          className={cx(
            'text-body leading-relaxed text-ink-mid',
            title !== undefined && title !== null && title.length > 0 && 'mt-3',
          )}
        >
          {children}
        </div>
      </article>
    );
  }
  return (
    <div
      className={cx(
        'rounded-lg border px-4 py-3 text-body leading-relaxed',
        tone === 'warning'
          ? 'border-warn-line bg-warn-tint text-warn'
          : 'border-line-soft bg-well text-ink-mid',
      )}
    >
      {title !== undefined && title !== null && title.length > 0 ? (
        <strong className="mb-1 block text-body font-medium text-ink">{title}</strong>
      ) : null}
      {children}
    </div>
  );
}

export function SnippetCard({
  title,
  caption,
  code,
  copyLabel,
  onCopy,
  testId,
}: {
  readonly title: string | null;
  readonly caption: string | null;
  readonly code: string;
  readonly copyLabel: string;
  readonly onCopy: () => void;
  readonly testId?: string;
}): JSX.Element {
  return (
    <article
      data-testid={testId}
      className="min-w-0 overflow-hidden rounded-lg border border-line-soft bg-well"
    >
      <div className="flex items-center justify-between gap-3 border-b border-line-soft px-3 py-1.5">
        <div className="min-w-0">
          {title !== null ? (
            <h3 className="m-0 text-label font-medium text-ink-mid">{title}</h3>
          ) : null}
          {caption !== null ? <p className="m-0 text-caption text-ink-low">{caption}</p> : null}
        </div>
        <Button variant="ghost" size="sm" onClick={onCopy}>
          <Icon name="content_copy" size="xs" />
          {copyLabel}
        </Button>
      </div>
      <pre className="m-0 overflow-x-auto px-3 py-2.5 font-mono text-body leading-relaxed text-ink">
        {code}
      </pre>
    </article>
  );
}

export function KeyValueList({
  title,
  rows,
}: {
  readonly title: string | null;
  readonly rows: readonly {
    readonly label: string;
    readonly value: string;
    readonly mono?: boolean;
  }[];
}): JSX.Element {
  const list = (
    <dl
      className={cx(
        'm-0 divide-y divide-line-soft',
        title !== null ? 'mt-3' : 'rounded-lg border border-line-soft',
      )}
    >
      {rows.map((row) => (
        <div
          key={`${row.label}:${row.value}`}
          className="grid grid-cols-1 gap-1 px-3 py-2 md:grid-cols-[9rem_minmax(0,1fr)]"
        >
          <dt className="text-label text-ink-low">{row.label}</dt>
          <dd
            className={cx(
              'm-0 min-w-0 break-words text-body text-ink-mid',
              row.mono === true && 'font-mono',
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
  if (title === null) return list;
  return (
    <article className="rounded-lg border border-line-soft bg-well p-4">
      <h3 className="m-0 text-body font-medium text-ink">{title}</h3>
      {list}
    </article>
  );
}

export function NumberedStep({
  n,
  title,
  children,
}: {
  readonly n: number;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <li className="flex gap-3 rounded-lg border border-line-soft bg-well p-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-raised font-mono text-label text-forest">
        {n}
      </span>
      <span className="min-w-0">
        <strong className="block text-body font-medium text-ink">{title}</strong>
        <span className="text-body leading-relaxed text-ink-mid">{children}</span>
      </span>
    </li>
  );
}

export function Checklist({
  title,
  items,
}: {
  readonly title: string | null;
  readonly items: readonly string[];
}): JSX.Element {
  return (
    <div className="rounded-lg border border-line-soft bg-well p-4">
      {title !== null ? <h3 className="m-0 text-body font-medium text-ink">{title}</h3> : null}
      <ol className={cx('m-0 flex list-none flex-col gap-2 p-0', title !== null && 'mt-3')}>
        {items.map((item, index) => (
          <li key={item} className="flex gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-raised font-mono text-caption text-forest">
              {index + 1}
            </span>
            <span className="text-body leading-relaxed text-ink-mid">{item}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function ActionLink({
  href,
  label,
  external,
}: {
  readonly href: string;
  readonly label: string;
  readonly external: boolean;
}): JSX.Element {
  const className =
    'flex flex-col gap-1 rounded-lg border border-line-soft bg-card p-4 transition-colors duration-150 hover:border-line-strong hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40';
  const body = <span className="text-body font-medium text-ink">{label}</span>;
  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {body}
      </a>
    );
  }
  const to = hrefToRouterPath(href);
  if (to.startsWith('/')) {
    return (
      <Link to={to} className={className}>
        {body}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {body}
    </a>
  );
}

export function tocChipClass(isActive: boolean): string {
  return cx(buttonClass(isActive ? 'primary' : 'secondary', 'sm'), isActive && 'scroll-mx-3');
}

export function tocItemClass(isActive: boolean): string {
  return cx(
    'flex items-center gap-2 rounded px-2.5 py-2.5 text-label font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
    isActive ? 'bg-forest-tint text-forest-deep' : 'text-ink-mid hover:bg-active/50 hover:text-ink',
  );
}

/**
 * Same-origin absolute URLs collapse to a path so react-router can
 * keep the session SPA; anything else is left as an href.
 */
export function hrefToRouterPath(href: string): string {
  if (href.startsWith('/')) return href;
  try {
    const url = new URL(href);
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    if (origin.length > 0 && url.origin === origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return href;
  }
  return href;
}
